<template>
    <div>
        <form method="post" :action="post_route" id="remote-form">
            <input type="hidden" name="_token" v-bind:value="csrf">

            <h2 class="h4 font-weight-bold">{{ $lang('Informacje podstawowe') }}</h2>
            <div class="row">
                <div class="col-md-6 form-group">
                    <datepicker-accessible
                        :label-single="$lang('Początek')"
                        custom-id="start_date"
                        name="start_date"
                        mode="date"
                        :use_pencil_checkbox="false"
                        :strong_label="false"
                        v-model="startDate"
                        :is_required="true"
                    ></datepicker-accessible>
                    <small class="text-red small-70" v-if="!validDate">
                        {{ $lang('Data musi być wcześniejsza niż data zakończenia') }}
                    </small>
                </div>
                <div class="col-md-6 form-group">
                    <datepicker-accessible
                        :label-single="$lang('Koniec')"
                        custom-id="end_date"
                        name="end_date"
                        mode="date"
                        :use_pencil_checkbox="false"
                        :strong_label="false"
                        v-model="endDate"
                        :is_required="true"
                    ></datepicker-accessible>
                    <small class="text-red small-70" v-if="!validDate">
                        {{ $lang('Data musi być późniejsza niż data rozpoczęcia') }}
                    </small>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6 form-group">
                    <datepicker-accessible
                        :label-single="$lang('Początek obowiązywania zmian')"
                        custom-id="effective_date"
                        name="effective_date"
                        mode="date"
                        :use_pencil_checkbox="false"
                        :strong_label="false"
                        v-model="effectiveDate"
                        :is_required="requiredEffectiveDate"
                    ></datepicker-accessible>
                    <small class="text-red small-70" v-if="!validEffectiveDate">
                        {{ $lang('Data musi być taka sama lub późniejsza niż data zakończenia') }}
                    </small>
                </div>
                <div class="col-md-6 form-group">
                    <label for="payment">{{ $lang('Płatność') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <select id="payment" name="payment" class="form-control" v-model="payment">
                        <option value="">{{ $lang('wybierz') }}</option>
                        <option v-for="payment in payments" :value="payment.id">{{ payment.name }}</option>
                    </select>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6 form-group">
                    <label for="job_type">{{ $lang('Typ prac zdalnych') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <select id="job_type" name="job_type" class="form-control" v-model="type">
                        <option value="">{{ $lang('wybierz') }}</option>
                        <option v-for="type in types" :value="type.id">{{ type.name }}</option>
                    </select>
                </div>
                <div class="col-md-6 form-group" v-if="type === 1">
                    <label for="version">{{ $lang('Numer nowej wersji systemu (XXXX.X.XX)') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <input class="form-control" type="text" id="version" name="version"
                           v-model="version"
                           :placeholder="$lang('Numer nowej wersji systemu (XXXX.X.XX)')">
                </div>
            </div>

            <h2 class="h4 font-weight-bold mt-3">{{ $lang('Uczestnicy') }}</h2>
            <div class="row">
                <div class="col-md-6 form-group">
                    <label for="library_id">{{ $lang('Biblioteka') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <select id="library_id" name="library_id" class="form-control" v-model="libraryID">
                        <option :value=0>{{ $lang('wybierz') }}</option>
                        <option v-for="library in libraries" :value="library.id">{{ library.name }}</option>
                    </select>
                </div>
                <div class="col-md-6 form-group">
                    <label for="client_id">{{ $lang('Zgłaszający') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <select id="client_id" name="client_id" class="form-control" v-model="clientID">
                        <option :value=0>{{ $lang('wybierz') }}</option>
                        <option v-for="client in clients" :value="client.id">{{ client.surname + ' ' + client.name }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6 form-group">
                    <label for="consultant_id">{{ $lang('Konsultant') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <select id="consultant_id" name="consultant_id" class="form-control" v-model="consultantID">
                        <option :value=0>{{ $lang('wybierz') }}</option>
                        <option :value=-1 v-if="type===1">{{ $lang('Brak zamówionych konsultacji') }}</option>
                        <option :value=-1 v-if="type===3">{{ $lang('Brak konsultanta') }}</option>
                        <option v-for="consultant in consultants" :value="consultant.id">{{
                                consultant.surname + ' ' +
                                consultant.name
                            }}
                        </option>
                    </select>
                </div>
                <div class="col-md-6 form-group">
                    <label for="technical_person_id">{{ $lang('Wykonawca') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <select id="technical_person_id" name="technical_person_id" class="form-control"
                            v-model="programmerID">
                        <option :value=0>{{ $lang('wybierz') }}</option>
                        <option v-for="programmer in programmers" :value="programmer.id">{{
                                programmer.surname + ' ' +
                                programmer.name
                            }}
                        </option>
                    </select>
                </div>
            </div>

            <h2 class="h4 font-weight-bold mt-3">{{ $lang('Ustalenia końcowe') }}</h2>
            <div class="row">
                <div class="col-md-6 form-group">
                    <label for="task_id">{{ $lang('Zgłoszenie') }}
                        <small v-if="requiredSosId" class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <input class="form-control" type="text" id="task_id" name="task_id"
                           v-model="sosID" @input="acceptNumber"
                           :placeholder="$lang('ID zgłoszenia')">
                    <div class="col-12 pl-1 mt-1 form-group" v-if="requiredSosId">
                        <input type="checkbox" id="no_task_id" name="no_task_id" v-model="noSosId">
                        <label for="no_task_id">{{$lang('Nie dotyczy')}}</label>
                    </div>
                </div>
                <div class="col-md-6 form-group">
                    <label for="jira_id">{{ $lang('Zlecenie') }}
                        <small class="text-red small-70">({{ $lang('wymagane') }})</small>
                    </label>
                    <input class="form-control" type="text" id="jira_id" name="jira_id"
                           v-model="jiraID"
                           :placeholder="$lang('Jira ID')">
                </div>
            </div>

            <div class="form-group">
                <label for="description">{{ $lang('Opis') }}</label>
                <textarea class="form-control" id="description" name="description" v-model="description"
                          rows="5"></textarea>
            </div>

            <div class="form-group mt-3" v-if="remoteExists">
                <h6 class="text-warning">{{this.remoteExistsMessage}}</h6>
            </div>

            <input id="change_task_status" ref="changeTaskStatusInput" name="change_task_status" :value="false" hidden>

            <button class="pull-right btn btn-primary  mt-3 mb-3"
                    type="button"
                    :disabled="valid === false"
                    @click="triggerClick()"
            >{{ $lang('Zapisz pracę') }}
            </button>
        </form>

        <div class="modal fade" id="changeTaskStatusModal" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $lang('Zmiana statusu zgłoszenia') }}</h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <span>{{ $lang('Czy chcesz zmienić status powiązanego zgłoszenia w Jira na')}}
                            <b>{{ $lang('Czeka na potwierdzenie') }}</b>
                            {{ $lang('dodając komentarz') + ':' }}
                            <br/><br/>
                            <span class="font-weight-bold">
                                {{ $lang('Wysłano prośbę o potwierdzenie poprawności wgranych zmian.')}}
                                <br/>
                                <span v-if="description !== ''">
                                    {{ $lang('Szczegóły') + ': ' + description }}
                                    <br/>
                                </span>
                                <span v-if="effectiveDate !== '-'">
                                    {{'***' + $lang('Możliwość weryfikacji i potwierdzenia pracy zdalnej od') + ':'}}
                                    {{ effectiveDateToShow }}
                                </span>
                            </span>
                        </span>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-default" @click.prevent="submitForm">{{
                                $lang('Nie')
                            }}
                        </button>
                        <button type="button" class="btn btn-primary" @click.prevent="setChangeStatusRequest"
                                data-dismiss="modal">{{
                                $lang('Tak')
                            }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "remote-add-form",
    props: {
        csrf: String,
        post_route: String,
        clients_update_route: String,
        check_if_remote_exists_route: String,
        check_if_task_status_can_be_changed: String,
        libraries: Array,
        consultants: Array,
        programmers: Array,
        persons: Array,
        payments: Array,
        types: Array,
        installation_type_id: Number,
        upload_type_id: Number,
        task: Object | Array,
        remote: Object | Array,
        today: String,
    },
    data() {
        return {
            clients: [],
            startDate: '-',
            endDate: '-',
            effectiveDate: '-',
            payment: '',
            type: '',
            version: '',
            libraryID: 0,
            clientID: 0,
            consultantID: 0,
            programmerID: 0,
            uploadType: 0,
            sosID: '',
            noSosId: false,
            jiraID: '',
            description: '',
            saveButtonClicked: false,
            remoteExists: false,
            remoteExistsMessage: '',
            taskCurrentJiraStatusName: '',
            effectiveDateToShow: null,
        }
    },
    methods: {
        updateClients() {
            let data = {};
            data['library_id'] = this.libraryID;
            let self = this;
            axios.post(self.clients_update_route, data)
                .then(function (response) {
                    self.clients = response.data;
                    self.clientID = (Object.keys(this.task).length !== 0) ? this.task.person_id : 0;
                })
                .catch(function (error) {
                    console.log(error);
                });
        },
        async triggerClick() {
            await this.checkIfTaskStatusCanBeChanged();

            if (this.taskStatusCanBeChanged) {
                $("#changeTaskStatusModal").modal('show');
            } else {
                this.saveButtonClicked = true;
                this.submitForm();
            }
        },
        submitForm() {
            document.getElementById('remote-form').submit();
        },
        checkIfPersonExistsInArray(array, personId) {
            return array.some(function (element) {
                return element.id === personId;
            });
        },
        checkIfRemoteExists() {
            let data = {};
            data['library_id'] = this.libraryID;
            data['task_id'] = this.sosID;
            let self = this;
            axios.post(self.check_if_remote_exists_route, data)
                .then(function (response) {
                    self.remoteExists = response.data['remote_exists'];
                    self.remoteExistsMessage = response.data['remote_exists_message'];
                })
                .catch(function (error) {
                    console.log(error);
                });
        },
        async checkIfTaskStatusCanBeChanged() {
            let self = this;
            let data = {};

            data['library_id'] = this.libraryID;
            data['sos_id'] = this.sosID;
            data['jira_id'] = this.jiraID;

            await axios.post(self.check_if_task_status_can_be_changed, data)
                .then(function (response) {
                    self.taskCurrentJiraStatusName = response.data.currentJiraStatusName;
                    self.taskStatusCanBeChanged = response.data.statusCanBeChanged;
                })
                .catch(function (error) {
                    console.log(error);
                });
        },
        setChangeStatusRequest() {
            this.$refs.changeTaskStatusInput.value = true;
            this.submitForm();
        },
        acceptNumber() {
            this.sosID = this.sosID.replace(/\D/g, '');
        }
    },
    watch: {
        libraryID: {
            immediate: true,
            handler() {
                if (this.libraryID > 0) {
                    this.updateClients();
                } else {
                    this.clients = [];
                }
                this.clientID = 0;
                if (this.sosID !== '') {
                    this.checkIfRemoteExists();
                }
            }
        },
        clients: function (val) {
            if (Object.keys(this.task).length === 0 && Object.keys(this.remote).length === 0) {
                this.clientID = 0;
            } else {
                if (Object.keys(this.task).length !== 0) {
                    this.clientID = this.task.person_id;
                } else if (Object.keys(this.remote).length !== 0) {
                    this.clientID = this.remote.client_id;
                }
            }

        },
        sosID: function () {
            if (this.sosID === '') {
                this.remoteExists = false;
                this.remoteExistsMessage = '';
            } else {
                this.checkIfRemoteExists();
            }
        },
        effectiveDate: function () {
            this.effectiveDateToShow = new Date(this.effectiveDate).toLocaleDateString('pl-PL');
        },
        type: function () {
            this.noSosId = false;
        },
        noSosId: function () {
            this.sosID = '';
        }
    },
    computed: {
        requiredSosId: function () {
            return this.type === this.upload_type_id;
        },
        requiredEffectiveDate: function () {
            return this.type === this.upload_type_id;
        },
        requiredVersion: function () {
            return this.type === this.installation_type_id;
        },
        valid: function () {
            return this.startDate !== '-' && this.endDate !== '-' && this.startDate <= this.endDate && this.payment !== '' &&
                this.validEffectiveDate && (this.requiredEffectiveDate && this.effectiveDate !== '-' || !this.requiredEffectiveDate) &&
                this.type !== '' && this.validVersion && this.libraryID !== 0 &&
                this.clientID !== 0 && this.consultantID !== 0 && this.programmerID !== 0 && this.saveButtonClicked === false &&
                (this.requiredSosId && (this.sosID !== '' || this.noSosId) || !this.requiredSosId) && this.jiraID !== '';
        },
        validDate: function () {
            if (this.endDate && this.startDate !== '-' && this.endDate !== '-') {
                return this.startDate <= this.endDate
            } else return true
        },
        validEffectiveDate: function () {
            if (this.effectiveDate !== '-' && this.endDate !== '-') {
                return this.effectiveDate >= this.endDate;
            } else {
                return true;
            }
        },
        validVersion: function () {
            if (this.requiredVersion) {
                if (this.version !== '') {
                    let versionRegExp = /[0-9]{4}\.[0-9]\.[0-9]{2}?$/;
                    return versionRegExp.test(this.version);
                }
                return false;
            }
            return true;
        }
    },
    created() {
        if (Object.keys(this.task).length === 0 && Object.keys(this.remote).length === 0) {
            this.startDate = '-';
            this.endDate = '-';
            this.effectiveDate = '-';
            this.payment = '';
            this.type = '';
            this.version = '';
            this.libraryID = 0;
            this.clientID = 0;
            this.consultantID = 0;
            this.programmerID = 0;
            this.uploadType = 0;
            this.sosID = '';
            this.jiraID = '';
            this.description = '';
        } else {
            if (Object.keys(this.task).length !== 0) {
                this.startDate = this.today;
                this.endDate = this.today;
                this.libraryID = this.task.library_id;
                if (this.checkIfPersonExistsInArray(this.consultants, this.task.consultant_id)) {
                    this.consultantID = this.task.consultant_id;
                }
                if (this.checkIfPersonExistsInArray(this.programmers, this.task.consultant_id)) {
                    this.programmerID = this.task.consultant_id;
                }
                this.sosID = this.task.id;
                this.jiraID = this.task.jira_id;
            } else if (Object.keys(this.remote).length !== 0) {
                this.startDate = this.remote.start_date;
                this.endDate = this.remote.end_date;
                if (this.remote.effective_date !== null) {
                    this.effectiveDate = this.remote.effective_date;
                }
                this.payment = this.remote.payment;
                this.type = this.remote.job_type;
                this.consultantID = this.remote.consultant_id;
                this.programmerID = this.remote.technical_person_id;
                this.sosID = this.remote.task_id;
                this.jiraID = this.remote.jira_id;
                this.description = this.remote.description;
            }
        }
    }
}
</script>
